import authService from './authService';
import { ensureOk, readData, readList, toQuery } from './apiResponse';
import type {
  AIAgent,
  AIAgentForm,
  AIBrandVoice,
  AIBudget,
  AIModelInfo,
  AIProvider,
  AIProviderForm,
  AIUsageDashboard,
  Integration,
  IntegrationHistory,
  TestResult,
} from '@/types/admin';

/** Admin → Integrações e Admin → IA (somente admin). */
class AdminService {
  private async api() {
    return authService.getApiClient();
  }

  // ------------------------------------------------------------ integrações

  /** GET /admin/integrations */
  async listIntegrations(): Promise<Integration[]> {
    const res = await (await this.api()).get('/admin/integrations');
    return readList<Integration>(res, 'Falha ao carregar as integrações');
  }

  /** PUT /admin/integrations/:key — values: chave → valor ("" limpa). */
  async saveIntegration(key: string, values: Record<string, string>): Promise<Integration> {
    const res = await (await this.api()).put(`/admin/integrations/${key}`, { values });
    return readData<Integration>(res, 'Falha ao salvar a integração');
  }

  /** POST /admin/integrations/:key/test */
  async testIntegration(key: string): Promise<TestResult> {
    const res = await (await this.api()).post(`/admin/integrations/${key}/test`);
    return readData<TestResult>(res, 'Falha ao testar a integração');
  }

  /** GET /admin/integrations/:key/history */
  async integrationHistory(key: string): Promise<IntegrationHistory[]> {
    const res = await (await this.api()).get(`/admin/integrations/${key}/history`);
    return readList<IntegrationHistory>(res, 'Falha ao carregar o histórico');
  }

  // ------------------------------------------------------------ IA: provedores

  /** GET /admin/ai/providers */
  async listProviders(): Promise<AIProvider[]> {
    const res = await (await this.api()).get('/admin/ai/providers');
    const data = await readData<{ providers: AIProvider[] | null }>(res, 'Falha ao carregar os provedores');
    return data?.providers ?? [];
  }

  /** POST /admin/ai/providers | PUT /admin/ai/providers/:id */
  async saveProvider(form: AIProviderForm, id?: number): Promise<AIProvider> {
    const api = await this.api();
    const res = id ? await api.put(`/admin/ai/providers/${id}`, form) : await api.post('/admin/ai/providers', form);
    return readData<AIProvider>(res, 'Falha ao salvar o provedor');
  }

  /** DELETE /admin/ai/providers/:id */
  async deleteProvider(id: number): Promise<void> {
    const res = await (await this.api()).delete(`/admin/ai/providers/${id}`);
    await ensureOk(res, 'Falha ao excluir o provedor');
  }

  /** POST /admin/ai/providers/:id/activate */
  async activateProvider(id: number, note: string): Promise<AIProvider> {
    const res = await (await this.api()).post(`/admin/ai/providers/${id}/activate`, { note });
    return readData<AIProvider>(res, 'Falha ao ativar o provedor');
  }

  /** POST /admin/ai/providers/:id/test */
  async testProvider(id: number): Promise<TestResult> {
    const res = await (await this.api()).post(`/admin/ai/providers/${id}/test`);
    return readData<TestResult>(res, 'Falha ao testar o provedor');
  }

  /** POST /admin/ai/models — lista os modelos para a chave do formulário (ou a gravada). */
  async listModels(form: Partial<AIProviderForm>, providerId?: number): Promise<AIModelInfo[]> {
    const res = await (await this.api()).post('/admin/ai/models', { ...form, provider_id: providerId ?? null });
    return readList<AIModelInfo>(res, 'Falha ao listar os modelos');
  }

  // ------------------------------------------------------------ IA: agentes, tom de voz, orçamento

  /** GET /admin/ai/agents */
  async listAgents(): Promise<AIAgent[]> {
    const res = await (await this.api()).get('/admin/ai/agents');
    return readList<AIAgent>(res, 'Falha ao carregar os agentes');
  }

  /** PUT /admin/ai/agents/:key */
  async saveAgent(key: string, form: AIAgentForm): Promise<AIAgent> {
    const res = await (await this.api()).put(`/admin/ai/agents/${key}`, form);
    return readData<AIAgent>(res, 'Falha ao salvar o agente');
  }

  /** GET /admin/ai/brand-voices */
  async listBrandVoices(): Promise<AIBrandVoice[]> {
    const res = await (await this.api()).get('/admin/ai/brand-voices');
    return readList<AIBrandVoice>(res, 'Falha ao carregar o tom de voz');
  }

  /** POST /admin/ai/brand-voices | PUT /admin/ai/brand-voices/:id */
  async saveBrandVoice(v: Partial<AIBrandVoice>, id?: number): Promise<AIBrandVoice> {
    const api = await this.api();
    const res = id ? await api.put(`/admin/ai/brand-voices/${id}`, v) : await api.post('/admin/ai/brand-voices', v);
    return readData<AIBrandVoice>(res, 'Falha ao salvar o tom de voz');
  }

  /** GET /admin/ai/budgets */
  async listBudgets(): Promise<AIBudget[]> {
    const res = await (await this.api()).get('/admin/ai/budgets');
    return readList<AIBudget>(res, 'Falha ao carregar o orçamento');
  }

  /** PUT /admin/ai/budgets */
  async saveBudgets(
    budgets: { scope: string; monthly_limit_usd: number | null; alert_percent: number; hard_block: boolean }[],
  ): Promise<AIBudget[]> {
    const res = await (await this.api()).put('/admin/ai/budgets', { budgets });
    return readList<AIBudget>(res, 'Falha ao salvar o orçamento');
  }

  /** GET /admin/ai/usage?from=&to=&purpose=&feature= */
  async usage(params: { from?: string; to?: string; purpose?: string; feature?: string }): Promise<AIUsageDashboard> {
    const res = await (await this.api()).get(`/admin/ai/usage${toQuery(params)}`);
    return readData<AIUsageDashboard>(res, 'Falha ao carregar o consumo');
  }

  /** POST /admin/ai/usage/sync */
  async syncProviderUsage(): Promise<TestResult> {
    const res = await (await this.api()).post('/admin/ai/usage/sync');
    return readData<TestResult>(res, 'Falha ao sincronizar o consumo dos provedores');
  }
}

export default new AdminService();
